// Globals
import { useCallback, useMemo } from 'react';

// Components
import { StyledButton, StyledInput, StyledWrapper } from './AmountSelectStyles';
import { MinusIcon, PlusIcon } from '@/icons';

// Helpers
import { subtractDecimal, addDecimal, toNumber } from '@/utils';
import { AmountSelectMeta } from '@/types';

type TAmountSelect = {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  precision?: number;
  onChange?: (amount: number | null, meta: { type: AmountSelectMeta }) => void;
  onInputChange?: (value: number | null) => void;
};

const AmountSelect = ({
  onChange,
  onInputChange,
  value,
  max = Number.MAX_SAFE_INTEGER,
  min = Number.MIN_SAFE_INTEGER,
  step = 1,
  precision = 1,
}: TAmountSelect) => {
  const isDecrementDisabled = useMemo(() => min > value - step, [value, min, step]);

  const isIncrementDisabled = useMemo(() => value >= max, [value, max]);

  const handleIncrement = useCallback(() => {
    if (isIncrementDisabled) return;
    const newCount = addDecimal(value, step, max, precision);
    onChange?.(newCount, { type: AmountSelectMeta.UP });
  }, [value, isIncrementDisabled, max, onChange, precision, step]);

  const handleDecrement = useCallback(() => {
    if (isDecrementDisabled) {
      return onChange?.(null, { type: AmountSelectMeta.DOWN });
    }
    const newCount = subtractDecimal(value, step, min, precision);
    onChange?.(newCount, { type: AmountSelectMeta.DOWN });
  }, [value, isDecrementDisabled, min, onChange, precision, step]);

  const handleChange = (val: unknown) => {
    onInputChange?.(toNumber(val));
  };

  return (
    <StyledWrapper $limitReached={isIncrementDisabled}>
      <StyledInput
        min={0}
        max={max}
        stringMode
        value={value}
        onChange={handleChange}
        addonBefore={<StyledButton size="small" icon={<MinusIcon />} onClick={handleDecrement} />}
        addonAfter={<StyledButton size="small" icon={<PlusIcon />} onClick={handleIncrement} />}
      />
    </StyledWrapper>
  );
};

export default AmountSelect;
